import { Stack, Typography } from '@mui/material';
import { INTEGRATION_PROVIDERS, type IntegrationProviders } from '@vy/types';
import { isEmptyArray, validateNotNullOrUndefined, values } from '@vy/utils';
import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useConnectedIntegrations } from '../../../hooks/requests/use-integrations';
import type { IntegrationDrawerRef } from './components/IntegrationDrawer';
import { useIntegrationContextProvider } from './hooks/use-integration-context-provider';
import { useIntegrationDrawerComponents } from './hooks/useIntegrationDrawerComponents';
import { IntegrationCardGroup } from './integrations-page-components/IntegrationCardGroup';
import type { getIntegrationMetadataResponse } from './integrations-page-types/integrations-types';
import { FormDrawer } from './NEW-integrations-page-components/FormDrawer';
import { IntegrationsContextProvider } from './NEW-integrations-page-components/IntegrationsContextProvider';
import { sortBy } from 'lodash';
import { VyLoader } from '../../../components/new-design/feedback/Loader/VyLoader';

const HIDDEN_INTEGRATIONS: IntegrationProviders[] = [INTEGRATION_PROVIDERS.connact];

const IntegrationsPage: React.FC = () => {
  const { data: connectedIntegrationNames = [], isLoading } = useConnectedIntegrations();
  const { connectedIntegrations, disconnectedIntegrations } = useMemo(
    () => getIntegrationMetadata(connectedIntegrationNames),
    [connectedIntegrationNames],
  );

  const drawerRef = useRef<IntegrationDrawerRef>(null);
  const { selectedIntegration, clearSelectedIntegration } = useIntegrationContextProvider();
  const selectedIntegrationDrawerComponents = useIntegrationDrawerComponents(selectedIntegration);

  useEffect(() => {
    if (drawerRef && selectedIntegration && selectedIntegrationDrawerComponents) {
      drawerRef.current?.openDrawer();
    } else if (!selectedIntegrationDrawerComponents) {
      drawerRef.current?.closeDrawer();
    }
  }, [drawerRef, selectedIntegration, selectedIntegrationDrawerComponents]);

  validateNotNullOrUndefined(connectedIntegrationNames, 'connectedIntegrations');

  return (
    <>
      <Helmet>
        <title>Integrations</title>
      </Helmet>
      <FormDrawer
        onClose={clearSelectedIntegration}
        ref={drawerRef}
        {...selectedIntegrationDrawerComponents}
      />
      <VyLayout>
        <Typography
          sx={{
            fontSize: 24,
            paddingY: 1.5
          }}>
          Integrations
        </Typography>

        <Stack direction="column" sx={{
          gap: "12px"
        }}>
          {isLoading ? (
            <>
              <VyLoader />
              <VyLoader height={640} />
            </>
          ) : (
            <>
              <IntegrationCardGroup
                connected
                sectionTitle="Your integrations"
                integrations={connectedIntegrations}
              />
              <IntegrationCardGroup
                connected={false}
                sectionTitle="More integrations to connect"
                integrations={disconnectedIntegrations}
              />
            </>
          )}
        </Stack>
      </VyLayout>
    </>
  );
};

function getIntegrationMetadata(
  connectedIntegrationNames?: IntegrationProviders[],
): getIntegrationMetadataResponse {
  const baseObject = { connectedIntegrations: [], disconnectedIntegrations: [] };
  const integrationValues = values(INTEGRATION_PROVIDERS);
  const visibleIntegrations = integrationValues.filter(
    (integration) => !HIDDEN_INTEGRATIONS.includes(integration),
  );
  const integrationKeys = sortBy(visibleIntegrations, (integration) =>
    integration === INTEGRATION_PROVIDERS.vayu ? 0 : 1,
  );

  if (isEmptyArray(connectedIntegrationNames)) {
    return {
      connectedIntegrations: [],
      disconnectedIntegrations: integrationKeys,
    };
  }

  return integrationKeys.reduce<getIntegrationMetadataResponse>(
    (mappedIntegrationKeys, currentIntegrationKey) => {
      if (connectedIntegrationNames.includes(currentIntegrationKey))
        return {
          connectedIntegrations: [...mappedIntegrationKeys.connectedIntegrations, currentIntegrationKey],
          disconnectedIntegrations: mappedIntegrationKeys.disconnectedIntegrations,
        };

      return {
        connectedIntegrations: mappedIntegrationKeys.connectedIntegrations,
        disconnectedIntegrations: [...mappedIntegrationKeys.disconnectedIntegrations, currentIntegrationKey],
      };
    },
    baseObject,
  );
}

const IntegrationsPageWithSuspenseBoundary = withSuspenseBoundary(() => (
  <IntegrationsContextProvider>
    <IntegrationsPage />
  </IntegrationsContextProvider>
));
export { IntegrationsPageWithSuspenseBoundary as IntegrationsPage };
